import React, {useState, useRef} from 'react'
import styled from 'styled-components'
import {Dialog, DialogActions, DialogContent, Button, TextField} from '@material-ui/core'
import {compressImage, getFileUrl} from 'utils/api'

const Label = styled.div`
	font-weight: 700;
	font-size: 13.5px;
	line-height: 1;
	margin-bottom: 5px;
`

const Img = styled.img`
	max-height: 300px;
	max-width: 300px;
`

const Input = styled.input`
    opacity: 0;
    width: 0;
`

const Empty = styled.span`
	color: #7c9198;
	font-style: italic;
	font-size: 13px;
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
`

const Placeholder = styled(Empty)`
    color: ${props => props.invalid ? '#f44336' : '#415dd4'};
    
	:hover {
		text-decoration: underline;
	}
`

const Container = styled.div`
    cursor: pointer;
	padding-top: 10px;
`

const Tabs = styled.div`
    display: flex;
    padding: 16px 0 30px;
`

const Tab = styled.div`
    text-transform: uppercase;
    border-bottom: 2px solid ${props => props.active ? '#415dd4' : 'transparent'};
    transition: border-color .3s cubic-bezier(.55, 0, .1, 1);
    color: ${props => props.active ? '#415dd4' : '#a9b9bd'};
    cursor: pointer;
    padding: 10px;
    width: 50%;
    text-align: center;
    font-size: 12px;
    letter-spacing: 1px;
`

const Image = ({label, defaultValue, fieldId, readonly, invalid, handleChange}) => {
    const [open, setOpen] = useState(false)
    const [type, setType] = useState('file')
    const [value, setValue] = useState('')
    const [file, setFile] = useState({name: '', data: ''})

    const input = useRef(null)

    const handleOpen = () => {
        setOpen(true)
        setType(defaultValue && defaultValue.indexOf('http') !== -1 ? 'url' : 'file')
    }

    const handleClose = () => {
        setOpen(false)
        setValue('')
        setFile({name: '', data: ''})
    }

    const handleUpdate = () => {
        if (value) {
            handleChange('c' + fieldId, value)
        } else {
            compressImage(file.data).then((string) => {
                handleChange('c' + fieldId, string)
            })
        }

		handleClose()
    }

    const handleDelete = () => {
        handleChange('c' + fieldId, '')
        handleClose()
    }

    const onChange = (e) => {
        const data = e.currentTarget.files[0]

        if (data) {
            const reader = new FileReader()
            reader.readAsDataURL(data)
            reader.onload = () => setFile({name: data.name, data: reader.result})
            reader.onerror = (error) => alert(error)
        }
    }

    const getImageUrl = (src) => {
        if (src.indexOf('data:') && src.indexOf('http')) {
            return getFileUrl(src)
        }

        return src
    }

	return (
		<div style={{marginBottom: '30px'}} data-id={invalid ? fieldId : null}>
			<Label dangerouslySetInnerHTML={{__html: label}} />
            <Container
                onClick={!readonly && handleOpen}
            >
                {defaultValue ?
                    <Img
                        src={getImageUrl(defaultValue)}
                        alt=''
                    />
                : readonly ?
					<Empty>
                        No value
                    </Empty>
				:
                    <Placeholder invalid={invalid}>
                        Click to upload image
                    </Placeholder>
                }
            </Container>
            {open &&
                <Dialog
                    open={open}
                    onClose={handleClose}
                    fullWidth={true}
                >
                    <DialogContent>
                        <Tabs>
                            <Tab active={type === 'file'} onClick={() => setType('file')}>
                                Upload file
                            </Tab>
                            <Tab active={type === 'url'} onClick={() => setType('url')}>
                                Upload from url
                            </Tab>
                        </Tabs>
                        {type === 'file' ?
                            <React.Fragment>
                                <TextField
                                    label='Path to local file'
                                    placeholder='Click to upload image'
                                    InputProps={{
                                        readOnly: true
                                    }}
                                    fullWidth
                                    InputLabelProps={{
                                        shrink: true,
										style: {
											fontWeight: 400,
											fontSize: '1rem',
											color: 'rgba(0, 0, 0, 0.54)'
										}
                                    }}
                                    onClick={() => input.current.click()}
                                    value={file.name}
                                />
                                <Input type='file'
                                    onChange={onChange}
                                    ref={input}
                                    accept="image/jpeg, image/png, image/gif"
                                />
                            </React.Fragment> :
                            <TextField
                                label='Image URL'
                                placeholder='Enter URL to file'
                                fullWidth
                                InputLabelProps={{
                                    shrink: true,
									style: {
										fontWeight: 400,
										fontSize: '1rem',
										color: 'rgba(0, 0, 0, 0.54)'
									}
                                }}
                                defaultValue={value ? value : defaultValue && defaultValue.indexOf('http') !== -1 ? defaultValue : ''}
                                onChange={(e) => setValue(e.currentTarget.value)}
                            />
                        }
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose} color="primary">
                            Cancel
                        </Button>
                        {defaultValue &&
                            <Button onClick={handleDelete} variant="contained" color="secondary">
                                Delete
                            </Button>
                        }
                        <Button variant="contained"
                            color="primary"
                            onClick={handleUpdate}
                            disabled={!value && !file.name}
                        >
                            Save
                        </Button>
                    </DialogActions>
                </Dialog>
            }
        </div>
	)
}

export default React.memo(Image)